import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SEO = ({ 
  title, 
  description, 
  keywords = [],
  type = 'website',
  image = 'https://audioandco.com/og-image.png',
  imageAlt = 'Audio & Co. | UK podcast management, book publishing and business websites built for SEO',
  canonical = null,
  author = 'Jo Day',
  publishedTime = null,
  modifiedTime = null,
  noindex = false
}) => {
  const location = useLocation();
  const baseUrl = 'https://audioandco.com';
  
  // Build clean canonical URL: strip www, strip query params, normalise path
  let currentUrl;
  if (canonical) {
    currentUrl = canonical;
  } else {
    const cleanPath = location.pathname === '/' ? '/' : location.pathname.replace(/\/+$/, '');
    currentUrl = `${baseUrl}${cleanPath}`;
  }

  // Core brand keywords present on every page
  const defaultKeywords = [
    'Audio & Co', 'audioandco', 'done for you services',
    'small business services UK', 'business visibility',
  ];

  const allKeywords = [...new Set([...defaultKeywords, ...keywords])].join(', ');

  useEffect(() => {
    // Set title
    document.title = title;

    // Set or update meta tags
    const updateMetaTag = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', allKeywords);
    
    // Robots - tell Google to index EVERYTHING (or noindex when requested)
    const robotsDirective = noindex
      ? 'noindex, follow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
    updateMetaTag('robots', robotsDirective);
    updateMetaTag('googlebot', robotsDirective);
    
    // Open Graph
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:image', image, true);
    updateMetaTag('og:image:alt', imageAlt, true);
    updateMetaTag('og:site_name', 'Audio & Co®', true);
    updateMetaTag('og:locale', 'en_GB', true);
    
    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:title', title, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', image, true);
    updateMetaTag('twitter:image:alt', imageAlt);
    
    // Additional SEO tags
    updateMetaTag('author', author);
    updateMetaTag('publisher', 'Audio & Co®');
    updateMetaTag('language', 'English');
    updateMetaTag('revisit-after', '3 days');
    updateMetaTag('rating', 'General');
    updateMetaTag('distribution', 'Global');
    updateMetaTag('coverage', 'Worldwide');
    
    // AI Content Declaration
    updateMetaTag('ai-content-declaration', 'Original content about podcast production, YouTube management, book publishing, and business services by Audio & Co.');
    
    // Article metadata (for blog posts)
    if (type === 'article') {
      updateMetaTag('article:author', author, true);
      if (publishedTime) {
        updateMetaTag('article:published_time', publishedTime, true);
      }
      if (modifiedTime) {
        updateMetaTag('article:modified_time', modifiedTime, true);
      }
    }
    
    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', currentUrl);

    // Alternate for UK
    let alternateLink = document.querySelector('link[rel="alternate"][hreflang="en-GB"]');
    if (!alternateLink) {
      alternateLink = document.createElement('link');
      alternateLink.setAttribute('rel', 'alternate');
      alternateLink.setAttribute('hreflang', 'en-GB');
      document.head.appendChild(alternateLink);
    }
    alternateLink.setAttribute('href', currentUrl);

  }, [title, description, allKeywords, type, image, imageAlt, currentUrl]);

  return null;
};

export default SEO;
